//@flow

// Vendors
import React, { useEffect, useState } from 'react';
import classnames from 'classnames';
import { ServiceIcon, Trans, LoadingDots } from '../../../components';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

//Actions
import { submitDocument, deleteDocument } from '../../../reducers/agency/dlr/dlrActions';
import { openModal } from '../../../reducers/modal/modalActions';

//components
import { InputCheckbox, InputFile } from '../Forms/Inputs';

// TexKit Components
import { TxAlert, TxAlertType } from 'texkit-ui';
import { TextButton } from 'texkit/dist/components/Buttons';

// Helpers
import { isMobileDevice } from '../../../lib/helpers';

// Styles
import './file-upload.scss';

type Props = {
  className?: string,
  isExpand?: Boolean,
  setIsExpand?: number,
  docIndex: number,
  documentDetails?: Object,
  setDocumentSections?: Object,
  maxFileSize: number,
  maxNumberOfFiles: number,
  acceptedFileExtensions: string,
  allowedMimeTypes: string[],
};

const FileUpload = ({
  className,
  isExpand,
  setIsExpand,
  docIndex,
  documentDetails,
  setDocumentSections,
  maxFileSize,
  maxNumberOfFiles,
  acceptedFileExtensions,
  allowedMimeTypes,
}: Props) => {
  const { t } = useTranslation('dps');
  const dispatch = useDispatch();
  const [documentsUploaded, setDocumentsUploaded] = useState([]);
  const [optionalFlag, setOptionalFlag] = useState(false);
  const [errorMessages, setErrorMessages] = useState([]);
  const [uploadingCount, setUploadingCount] = useState(0);


  useEffect(() => {
    setDocumentSections(prev => {
      const newSections = [...prev];
      const attachedFiles = documentsUploaded.filter(f => f.documentId);
      const errorFile = documentsUploaded.find(f => f.errorType);
      if (optionalFlag) {
        newSections[docIndex].documentStatus = 'Not_Required';
        newSections[docIndex].attachedCount = 0;
        newSections[docIndex].errorType = undefined;
      } else if (errorFile) {
        newSections[docIndex].documentStatus = attachedFiles.length > 0 ? 'Attached' : 'Not_Started';
        newSections[docIndex].attachedCount = attachedFiles.length;
        newSections[docIndex].errorType = errorFile.errorType;
      } else if (attachedFiles.length > 0) {
        newSections[docIndex].documentStatus = 'Attached';
        newSections[docIndex].attachedCount = attachedFiles.length;
        newSections[docIndex].errorType = undefined;
      } else {
        newSections[docIndex].documentStatus = 'Not_Started';
        newSections[docIndex].attachedCount = 0;
        newSections[docIndex].errorType = undefined;
      }
      return newSections;
    });
  }, [documentsUploaded, optionalFlag]);

  const updateState = async (result, response, errorType = null) => {
    const tempAttachedDocuments = [];
    tempAttachedDocuments[docIndex] = {};
    tempAttachedDocuments[docIndex].attached = true;
    tempAttachedDocuments[docIndex].fileName = result.name;
    tempAttachedDocuments[docIndex].fileType = result.type;
    tempAttachedDocuments[docIndex].fileSize = result.size;
    tempAttachedDocuments[docIndex].expanded = true;
    tempAttachedDocuments[docIndex].result = result;
    tempAttachedDocuments[docIndex].documentId =
      response && response.data.documentId;
    tempAttachedDocuments[docIndex].errorType = errorType;
    setDocumentsUploaded(documentsUploaded => [
      ...documentsUploaded,
      tempAttachedDocuments[docIndex],
    ]);
  };
  
  const ERROR_MESSAGE_MAP = {
    malware: {
      id: 'malware',
      title: 'dps_SurrenderDLForID_Malware_Error_Title',
      description: 'dps_SurrenderDLForID_Error_Subheading',
      type: TxAlertType.Error,
    },
    fileNotAccepted: {
      id: 'fileNotAccepted',
      title: 'dps_SurrenderDLForID_File_Not_Accepted_Error_Title',
      description: 'dps_SurrenderDLForID_Error_Subheading',
      type: TxAlertType.Error,
    },
  };

  const onFileChange = event => {
    event.preventDefault();
    const files = event.target.files;

    if (files) {
      const newFiles: File[] = Array.from(files);

      const invalidFiles = newFiles.filter(
        file => !allowedMimeTypes.includes(file.type)
      );
      if (invalidFiles.length > 0) {
        dispatch(openModal('UnsupportedFilesModal'));
        return;
      }

      const currentUploadedFilesCount = documentsUploaded.length;
      if (currentUploadedFilesCount + newFiles.length > maxNumberOfFiles) {
        dispatch(openModal('TooManyFilesModal'));
        return;
      }

      const combinedFileSize =
        documentsUploaded.reduce((acc, file) => acc + file.fileSize, 0) +
        newFiles.reduce((acc, file) => acc + file.size, 0);

      if (combinedFileSize > maxFileSize) {
        dispatch(openModal('FileSizeRestrictionModal'));
        return;
      }

      newFiles.forEach(file => {
        setUploadingCount(prev => prev + 1);
        const reader = new FileReader();
        reader.readAsArrayBuffer(file);
        reader.onload = () => {
          const documentBody = new FormData();
          documentBody.append('file', file);
          documentBody.append('fileName', file.name);
          documentBody.append('documentType', documentDetails.documentType);

          dispatch(submitDocument(documentBody))
            .then(response => {
              if (!response || !response.data || !response.data.documentId) {
                dispatch(openModal('ServerErrorModal'));
                return;
              }
              updateState(file, response);
            })
            .catch(error => {
              const body = error?.body || error;
              const REF = body?.errors?.REF?.[0];
              if (error && error.response && error.response.status === 400) {
                if (body && body.status === 'SCAN_ERROR') {
                  updateState(file, null, 'malware');
                  setErrorMessages(prev => [
                    ...prev,
                    ERROR_MESSAGE_MAP.malware,
                  ]);
                } else if (
                  body &&
                  body.errorDescription === 'File not accepted'
                ) {
                  updateState(file, null, 'fileNotAccepted');
                  setErrorMessages(prev => [
                    ...prev,
                    ERROR_MESSAGE_MAP.fileNotAccepted,
                  ]);
                } else {
                  dispatch(openModal('ServerErrorModal', { REF }));
                }
              } else {
                dispatch(openModal('ServerErrorModal', {REF}));
              }
            })
            .finally(() => {
              setUploadingCount(prev => prev - 1);
            });
        };
        reader.onerror = () => {
          setUploadingCount(prev => prev - 1);
          console.log('File reading failed', file.name);
        };
      });
      event.target.value = '';
    }
  };

  const getConvertedSize = filesize =>
    `${(filesize / (1024 * 1024)).toFixed(2)} MB`;

  const removeFile = (event, fileIndex) => {
    event.stopPropagation();
    const documentToRemove = documentsUploaded[fileIndex];
    const errorType = documentToRemove.errorType;

    const updateErrorMessages = remainingDocs => {
      const activeErrors = [];
      if (remainingDocs.some(doc => doc.errorType === 'malware')) {
        activeErrors.push(ERROR_MESSAGE_MAP.malware);
      }
      if (remainingDocs.some(doc => doc.errorType === 'fileNotAccepted')) {
        activeErrors.push(ERROR_MESSAGE_MAP.fileNotAccepted);
      }
      setErrorMessages(activeErrors);
    };

    if (!documentToRemove || !documentToRemove.documentId) {
      setDocumentsUploaded(documentsUploaded => {
        const newDocuments = [...documentsUploaded];
        newDocuments.splice(fileIndex, 1);
        updateErrorMessages(newDocuments);
        return newDocuments;
      });
      return;
    }
    dispatch(deleteDocument(documentToRemove.documentId))
      .then(() => {
        setDocumentsUploaded(documentsUploaded => {
          const newDocuments = [...documentsUploaded];
          newDocuments.splice(fileIndex, 1);
          updateErrorMessages(newDocuments);
          return newDocuments;
        });
      })
        .catch(error => {
          const REF = error?.body?.errors?.REF?.[0];
          dispatch(openModal('ServerErrorModal', { REF }));
        });
  };

  const optionFlagChange = event => {
    const checked = event.target.checked;
    setOptionalFlag(checked);
    if (checked) {
      const errorFiles = documentsUploaded.filter(doc => !doc.documentId);
      if (errorFiles.length > 0) {
        setDocumentsUploaded(prev => prev.filter(doc => doc.documentId));
        setErrorMessages([]);
      }
      documentsUploaded.forEach(doc => {
        if (doc.documentId) {
          dispatch(deleteDocument(doc.documentId))
            .then(() => {
              setDocumentsUploaded(prev => prev.filter(d => d.documentId !== doc.documentId));
            })
            .catch(error => {
              const REF = error?.body?.errors?.REF?.[0];
              dispatch(openModal('ServerErrorModal', { REF }));
            });
        }
      });
    }
  };

  const getStatusIcon = () => {
    const attachedFiles = documentsUploaded.filter(f => f.documentId);
    const errorFiles  = documentsUploaded.filter(f => !f.documentId);

    if (optionalFlag) return 'close';
    if (attachedFiles.length > 0 && errorFiles.length > 0) return 'edit-gray';
    if (attachedFiles.length > 0) return 'file-verified';
    return 'attachment';
  };

  return (
    <div className={classnames('document-container', !isExpand && 'expanded')}>
      <div className="header-section">
        <div>
          <div className="title">
            <Trans file="dps" id={documentDetails.title} />
          </div>
          <div className="document-status">
            <ServiceIcon className="status-icon" type={getStatusIcon()} />
            <span className="status">
              {documentDetails.documentStatus === 'Attached' ? (
                <Trans
                  file="dps"
                  id="dps_Number_Of_Files_Attached"
                  fallback={`${documentDetails.attachedCount ||
                    documentsUploaded.length} Attached`}
                  args={[documentDetails.attachedCount]}
                />
              ) : (
                <Trans file="dps" id={documentDetails.documentStatus} />
              )}
            </span>
          </div>
        </div>
        <div onClick={() => setIsExpand(isExpand ? -1 : docIndex)}>
          <ServiceIcon
            className="arrow-icon"
            type={isExpand ? 'chevron-up' : 'chevron-down'}
          />
        </div>
      </div>
      {isExpand && (
        <>
          <div>
            <TextButton
              onClick={event => {
                event.preventDefault();
                dispatch(openModal(documentDetails.learnMoreModal));
              }}
            >
              <Trans file="Labels" id="LearnMore" fallback="LearnMore" />
            </TextButton>
          </div>
          {documentDetails.optional && (
            <InputCheckbox
              name="optional-check"
              checked={optionalFlag}
              label={<Trans file="dps" id={documentDetails.declaration} />}
              onChange={event => optionFlagChange(event)}
            />
          )}
          {documentsUploaded.length < maxNumberOfFiles && (
            <div
              className={classnames(
                'file-upload-container',
                className,
                optionalFlag || uploadingCount > 0 ? 'disabled' : ''
              )}
            >
              <InputFile
                type="file"
                name={`file-upload${docIndex}`}
                onChange={onFileChange}
                className={classnames('file-upload', className)}
                id={`fileupload-${docIndex}`}
                accept={acceptedFileExtensions}
                disabled={optionalFlag || uploadingCount > 0}
                multiple
                noWrapper
              />
              <div className="file-upload-content">
                <TextButton className="add-file-button">
                  <ServiceIcon className="add-file-icon" type="file-upload" />
                </TextButton>
                <div className="add-file-text">
                  <Trans
                    file="dps"
                    id={isMobileDevice() ? 'Tap_To_Add_Files' : 'Add_Files'}
                    fallback={
                      isMobileDevice()
                        ? 'Tap to add your files.'
                        : 'Click to add your files.'
                    }
                  />
                </div>
                <div className="add-file-desc">
                  <Trans
                    file="dps"
                    id="File_Types_And_Size"
                    fallback="Supported file types: PDF, JPG, JPEG."
                  />
                </div>
              </div>
            </div>
          )}
          {documentsUploaded.length < maxNumberOfFiles && (
            <div className="document-container-footer">
              <Trans
                file="dps"
                id="Max_Files_Description"
                fallback="You can attach up to 3 files per document type. The files will be combined in the order they appear."
              />
            </div>
          )}
          <div className="document-review-section">
            {documentsUploaded.map((d, index) => (
              <div
                key={d.filename}
                className={classnames(
                  'file_section',
                  d.documentId ? 'file-attached' : '',
                  !d.documentId ? 'error-file' : ''
                )}
              >
                <div>
                  <div className="attached-file-Name">{d.fileName}</div>
                  <div className="file-size">
                    {getConvertedSize(d.fileSize)}
                  </div>
                </div>
                <div onClick={event => removeFile(event, index)}>
                  <ServiceIcon
                    className="status-icon"
                    type={!d.documentId ? 'close-red' : 'close-blue'}
                  />
                </div>
              </div>
            ))}
            {uploadingCount > 0 && (
              <div className="uploading-section">
                <LoadingDots />
              </div>
            )}
          </div>

          {errorMessages.map((errorMessage, index) => (
            <TxAlert
              key={errorMessage.id}
              type={errorMessage.type}
              className="lockout-warning"
              heading={<Trans file="dps" id={errorMessage.title} />}
            >
              <Trans file="dps" id={errorMessage.description} />
            </TxAlert>
          ))}
        </>
      )}
    </div>
  );
};

export default FileUpload;
